import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { formatDate } from '../lib/utils';
import {
  Plus,
  Users,
  Edit2,
  Trash2,
  UserPlus,
  Trophy,
  BookOpen,
  Mic,
  Music,
  Leaf,
  FlaskConical,
  Phone,
  Mail,
  CalendarDays,
  GraduationCap,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import ExportButton from '../components/ExportButton';
import SchoolClubsSummaryCards from '../components/clubs/SchoolClubsSummaryCards';
import AllClubMembersTab from '../components/clubs/AllClubMembersTab';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CLUB_ICONS = {
  math_science: FlaskConical,
  debate: Mic,
  literature: BookOpen,
  music_art: Music,
  environmental: Leaf,
};

const CLUB_COLORS = {
  math_science: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  debate: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  literature: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  music_art: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300',
  environmental: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
};

const PROJECT_STATUS_COLORS = {
  in_progress: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  planned: 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-300',
};

const emptyClubForm = {
  club_type: 'math_science',
  name: '',
  teacher_in_charge: '',
  teacher_contact: '',
  description: '',
  current_project: '',
  project_description: '',
  project_status: 'in_progress',
  project_start_date: '',
  project_end_date: '',
};

const emptyMemberForm = {
  club_id: '',
  student_name: '',
  role: 'Member',
  project_role: '',
  grade_class: '',
  parent_name: '',
  parent_contact: '',
  parent_email: '',
  notes: '',
};

const SchoolClubsPage = () => {
  const { hasRole } = useAuth();
  const canEdit = hasRole(['admin', 'school_head', 'project_manager']);

  const [clubTypes, setClubTypes] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClub, setSelectedClub] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const [clubDialog, setClubDialog] = useState(false);
  const [memberDialog, setMemberDialog] = useState(false);
  const [editingClub, setEditingClub] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [clubForm, setClubForm] = useState(emptyClubForm);
  const [memberForm, setMemberForm] = useState(emptyMemberForm);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [typesRes, clubsRes, membersRes] = await Promise.all([
        axios.get(`${API_URL}/api/school-clubs/types`),
        axios.get(`${API_URL}/api/school-clubs`),
        axios.get(`${API_URL}/api/school-clubs/members/list`),
      ]);
      setClubTypes(typesRes.data);
      setClubs(clubsRes.data);
      setMembers(membersRes.data);
    } catch (err) {
      toast.error('Failed to load school clubs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const openCreateClub = () => {
    setEditingClub(null);
    setClubForm(emptyClubForm);
    setClubDialog(true);
  };

  const openEditClub = (club) => {
    setEditingClub(club);
    setClubForm({
      club_type: club.club_type,
      name: club.name || '',
      teacher_in_charge: club.teacher_in_charge || '',
      teacher_contact: club.teacher_contact || '',
      description: club.description || '',
      current_project: club.current_project || '',
      project_description: club.project_description || '',
      project_status: club.project_status || 'in_progress',
      project_start_date: club.project_start_date || '',
      project_end_date: club.project_end_date || '',
    });
    setClubDialog(true);
  };

  const submitClub = async () => {
    if (!clubForm.club_type) {
      toast.error('Please select a club type');
      return;
    }
    try {
      const payload = { ...clubForm };
      if (editingClub) {
        await axios.put(`${API_URL}/api/school-clubs/${editingClub.id}`, payload);
        toast.success('Club updated');
      } else {
        await axios.post(`${API_URL}/api/school-clubs`, payload);
        toast.success('Club created');
      }
      setClubDialog(false);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save club');
    }
  };

  const deleteClub = async (clubId) => {
    if (!window.confirm('Delete this club and all its members?')) return;
    try {
      await axios.delete(`${API_URL}/api/school-clubs/${clubId}`);
      toast.success('Club deleted');
      if (selectedClub?.id === clubId) setSelectedClub(null);
      loadAll();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const openCreateMember = (clubId) => {
    setEditingMember(null);
    setMemberForm({ ...emptyMemberForm, club_id: clubId });
    setMemberDialog(true);
  };

  const openEditMember = (member) => {
    setEditingMember(member);
    setMemberForm({
      club_id: member.club_id,
      student_name: member.student_name,
      role: member.role || 'Member',
      project_role: member.project_role || '',
      grade_class: member.grade_class || '',
      parent_name: member.parent_name || '',
      parent_contact: member.parent_contact || '',
      parent_email: member.parent_email || '',
      notes: member.notes || '',
    });
    setMemberDialog(true);
  };

  const submitMember = async () => {
    if (!memberForm.student_name) {
      toast.error('Student name is required');
      return;
    }
    try {
      if (editingMember) {
        await axios.put(`${API_URL}/api/school-clubs/members/${editingMember.id}`, memberForm);
        toast.success('Member updated');
      } else {
        await axios.post(`${API_URL}/api/school-clubs/members`, memberForm);
        toast.success('Member added');
      }
      setMemberDialog(false);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save member');
    }
  };

  const deleteMember = async (memberId) => {
    if (!window.confirm('Remove this member?')) return;
    try {
      await axios.delete(`${API_URL}/api/school-clubs/members/${memberId}`);
      toast.success('Removed');
      loadAll();
    } catch (err) {
      toast.error('Failed to remove');
    }
  };

  const getMemberCount = (clubId) => members.filter((m) => m.club_id === clubId).length;
  const clubMembers = selectedClub ? members.filter((m) => m.club_id === selectedClub.id) : [];

  const getClubLabel = (type) =>
    clubTypes.find((t) => t.value === type)?.label || type;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-muted-foreground">Loading school clubs...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="school-clubs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Trophy className="h-8 w-8 text-amber-500" />
            School Clubs
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage clubs, projects, members and parent contacts
          </p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <ExportButton path="exports/list/school-clubs" filenameBase="suubi_school_clubs" label="Export Clubs" testId="clubs-export-btn" />
            <ExportButton path="exports/list/club-members" filenameBase="suubi_club_members" label="Export Members" testId="club-members-export-btn" />
            <Button onClick={openCreateClub} data-testid="add-club-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Club
            </Button>
          </div>
        )}
        {!canEdit && (
          <div className="flex gap-2">
            <ExportButton path="exports/list/school-clubs" filenameBase="suubi_school_clubs" label="Export Clubs" />
            <ExportButton path="exports/list/club-members" filenameBase="suubi_club_members" label="Export Members" />
          </div>
        )}
      </div>

      {/* Summary cards — extracted to /components/clubs/SchoolClubsSummaryCards.jsx */}
      <SchoolClubsSummaryCards clubs={clubs} members={members} />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Clubs Overview</TabsTrigger>
          <TabsTrigger value="all-members" data-testid="tab-all-members">All Members</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          {clubs.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                No clubs yet. {canEdit && 'Click "Add Club" to create the first one.'}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {clubs.map((club) => {
                const Icon = CLUB_ICONS[club.club_type] || Trophy;
                const memberCount = getMemberCount(club.id);
                return (
                  <Card
                    key={club.id}
                    className={cn(
                      'hover:shadow-md transition-shadow cursor-pointer',
                      selectedClub?.id === club.id && 'ring-2 ring-primary'
                    )}
                    onClick={() => {
                      setSelectedClub(club);
                    }}
                    data-testid={`club-card-${club.club_type}`}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className={cn('p-2 rounded-lg', CLUB_COLORS[club.club_type])}>
                            <Icon className="h-6 w-6" />
                          </div>
                          <div>
                            <CardTitle className="text-lg">{club.name}</CardTitle>
                            <CardDescription>
                              {getClubLabel(club.club_type)}
                            </CardDescription>
                          </div>
                        </div>
                        {canEdit && (
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => { e.stopPropagation(); openEditClub(club); }}
                              data-testid={`edit-club-${club.id}`}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => { e.stopPropagation(); deleteClub(club.id); }}
                              data-testid={`delete-club-${club.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm">
                      {club.teacher_in_charge && (
                        <div className="flex items-center gap-2">
                          <GraduationCap className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">Teacher:</span> {club.teacher_in_charge}
                          {club.teacher_contact && (
                            <span className="text-muted-foreground">· {club.teacher_contact}</span>
                          )}
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span>{memberCount} member{memberCount !== 1 ? 's' : ''}</span>
                      </div>
                      {club.current_project && (
                        <div className="border-t pt-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="font-medium">Current Project</span>
                            <Badge className={PROJECT_STATUS_COLORS[club.project_status] || ''}>
                              {(club.project_status || 'in_progress').replace('_', ' ')}
                            </Badge>
                          </div>
                          <p className="font-semibold">{club.current_project}</p>
                          {club.project_description && (
                            <p className="text-muted-foreground text-xs">{club.project_description}</p>
                          )}
                          {(club.project_start_date || club.project_end_date) && (
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <CalendarDays className="h-3 w-3" />
                              {club.project_start_date && formatDate(club.project_start_date)}
                              {club.project_end_date && ` → ${formatDate(club.project_end_date)}`}
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Members of selected club */}
          {selectedClub && (
            <Card data-testid="selected-club-members">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>{selectedClub.name} — Members</CardTitle>
                  <CardDescription>
                    {clubMembers.length} member{clubMembers.length !== 1 ? 's' : ''}
                  </CardDescription>
                </div>
                {canEdit && (
                  <Button
                    onClick={() => openCreateMember(selectedClub.id)}
                    data-testid="add-member-btn"
                  >
                    <UserPlus className="h-4 w-4 mr-2" /> Add Member
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {clubMembers.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No members yet.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Student</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead>Project Role</TableHead>
                          <TableHead>Grade/Class</TableHead>
                          <TableHead>Parent</TableHead>
                          <TableHead>Contact</TableHead>
                          {canEdit && <TableHead className="text-right">Actions</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {clubMembers.map((m) => (
                          <TableRow key={m.id} data-testid={`member-row-${m.id}`}>
                            <TableCell className="font-medium">{m.student_name}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{m.role}</Badge>
                            </TableCell>
                            <TableCell>{m.project_role || '—'}</TableCell>
                            <TableCell>{m.grade_class || '—'}</TableCell>
                            <TableCell>{m.parent_name || '—'}</TableCell>
                            <TableCell>
                              <div className="flex flex-col text-xs">
                                {m.parent_contact && (
                                  <span className="flex items-center gap-1">
                                    <Phone className="h-3 w-3" /> {m.parent_contact}
                                  </span>
                                )}
                                {m.parent_email && (
                                  <span className="flex items-center gap-1 text-muted-foreground">
                                    <Mail className="h-3 w-3" /> {m.parent_email}
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            {canEdit && (
                              <TableCell className="text-right">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => openEditMember(m)}
                                  data-testid={`edit-member-${m.id}`}
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => deleteMember(m.id)}
                                  data-testid={`delete-member-${m.id}`}
                                >
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="all-members" className="space-y-4">
          {/* Extracted to /components/clubs/AllClubMembersTab.jsx */}
          <AllClubMembersTab clubs={clubs} members={members} clubColors={CLUB_COLORS} />
        </TabsContent>
      </Tabs>

      {/* Club Dialog */}
      <Dialog open={clubDialog} onOpenChange={setClubDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingClub ? 'Edit Club' : 'Create School Club'}</DialogTitle>
            <DialogDescription>
              Each club has a teacher in charge and an ongoing project.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Club Type *</Label>
                <Select
                  value={clubForm.club_type}
                  onValueChange={(v) => setClubForm({ ...clubForm, club_type: v })}
                >
                  <SelectTrigger data-testid="club-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {clubTypes.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Custom Name (optional)</Label>
                <Input
                  value={clubForm.name}
                  onChange={(e) => setClubForm({ ...clubForm, name: e.target.value })}
                  placeholder="Defaults to club type label"
                  data-testid="club-name-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Teacher in Charge</Label>
                <Input
                  value={clubForm.teacher_in_charge}
                  onChange={(e) => setClubForm({ ...clubForm, teacher_in_charge: e.target.value })}
                  data-testid="teacher-input"
                />
              </div>
              <div className="space-y-1">
                <Label>Teacher Contact</Label>
                <Input
                  value={clubForm.teacher_contact}
                  onChange={(e) => setClubForm({ ...clubForm, teacher_contact: e.target.value })}
                  placeholder="Phone or email"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Description</Label>
              <Textarea
                value={clubForm.description}
                onChange={(e) => setClubForm({ ...clubForm, description: e.target.value })}
                rows={2}
              />
            </div>

            <div className="border-t pt-3 space-y-3">
              <h4 className="font-semibold">Current Project</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1 col-span-2">
                  <Label>Project Title</Label>
                  <Input
                    value={clubForm.current_project}
                    onChange={(e) => setClubForm({ ...clubForm, current_project: e.target.value })}
                    data-testid="project-title-input"
                  />
                </div>
                <div className="space-y-1 col-span-2">
                  <Label>Project Description</Label>
                  <Textarea
                    value={clubForm.project_description}
                    onChange={(e) => setClubForm({ ...clubForm, project_description: e.target.value })}
                    rows={2}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Status</Label>
                  <Select
                    value={clubForm.project_status}
                    onValueChange={(v) => setClubForm({ ...clubForm, project_status: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="planned">Planned</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div />
                <div className="space-y-1">
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={clubForm.project_start_date}
                    onChange={(e) => setClubForm({ ...clubForm, project_start_date: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={clubForm.project_end_date}
                    onChange={(e) => setClubForm({ ...clubForm, project_end_date: e.target.value })}
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClubDialog(false)}>Cancel</Button>
            <Button onClick={submitClub} data-testid="submit-club-btn">
              {editingClub ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Member Dialog */}
      <Dialog open={memberDialog} onOpenChange={setMemberDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingMember ? 'Edit Member' : 'Add Club Member'}</DialogTitle>
            <DialogDescription>
              Member name, role, and parent contact details.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1 col-span-2">
                <Label>Student Name *</Label>
                <Input
                  value={memberForm.student_name}
                  onChange={(e) => setMemberForm({ ...memberForm, student_name: e.target.value })}
                  data-testid="member-name-input"
                />
              </div>
              <div className="space-y-1">
                <Label>Role in Club</Label>
                <Select
                  value={memberForm.role}
                  onValueChange={(v) => setMemberForm({ ...memberForm, role: v })}
                >
                  <SelectTrigger data-testid="member-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="President">President</SelectItem>
                    <SelectItem value="Vice President">Vice President</SelectItem>
                    <SelectItem value="Secretary">Secretary</SelectItem>
                    <SelectItem value="Treasurer">Treasurer</SelectItem>
                    <SelectItem value="Member">Member</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Grade / Class</Label>
                <Input
                  value={memberForm.grade_class}
                  onChange={(e) => setMemberForm({ ...memberForm, grade_class: e.target.value })}
                  placeholder="e.g. P.6 / S.3"
                />
              </div>
              <div className="space-y-1 col-span-2">
                <Label>Project Role</Label>
                <Input
                  value={memberForm.project_role}
                  onChange={(e) => setMemberForm({ ...memberForm, project_role: e.target.value })}
                  placeholder="Their specific role in the current project"
                />
              </div>
            </div>

            <div className="border-t pt-3 space-y-3">
              <h4 className="font-semibold">Parent / Guardian Contact</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1 col-span-2">
                  <Label>Parent / Guardian Name</Label>
                  <Input
                    value={memberForm.parent_name}
                    onChange={(e) => setMemberForm({ ...memberForm, parent_name: e.target.value })}
                    data-testid="parent-name-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Phone</Label>
                  <Input
                    value={memberForm.parent_contact}
                    onChange={(e) => setMemberForm({ ...memberForm, parent_contact: e.target.value })}
                    data-testid="parent-contact-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={memberForm.parent_email}
                    onChange={(e) => setMemberForm({ ...memberForm, parent_email: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div className="space-y-1">
              <Label>Notes</Label>
              <Textarea
                value={memberForm.notes}
                onChange={(e) => setMemberForm({ ...memberForm, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMemberDialog(false)}>Cancel</Button>
            <Button onClick={submitMember} data-testid="submit-member-btn">
              {editingMember ? 'Update' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SchoolClubsPage;
